import type { RuntypeBase } from 'runtypes/lib/runtype';
import type {
	FetchMailInvitationPayload,
	InvitationFacts,
	MailInvitationParticipation,
	MailInvitationQueryWrapper,
	RefetchMailInvitationPayload,
	RespondToMailInvitationPayload,
	RespondToMailInvitationResult,
	UnsubscribeFetchMailInvitationPayload,
} from 'src/types/mailInvitation';

import { inject } from 'vue';

import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { RespondToTheMailInvitationCommand } from 'src/command/RespondToTheMailInvitationCommand';
import { GetTheMailInvitationForTheMailQuery } from 'src/query/GetTheMailInvitationForTheMailQuery';
import { MailInvitationQueryWrapperRunType } from 'src/runtypes/mailInvitation';
import { AddReceiverGroupMembersCommand } from 'src/stores/mail/commands/AddReceiverGroupMembersCommand';
import { ConvertAttachmentToDocumentCommand } from 'src/stores/mail/commands/ConvertAttachmentToDocumentCommand';
import { CreateReceiverGroupCommand } from 'src/stores/mail/commands/CreateReceiverGroupCommand';
import { DeleteReceiverGroupCommand } from 'src/stores/mail/commands/DeleteReceiverGroupCommand';
import { DeleteTheMailTemplateOrDraftCommand } from 'src/stores/mail/commands/DeleteTheMailTemplateOrDraftCommand';
import { MailDeleteByAggregateIdCommand } from 'src/stores/mail/commands/MailDeleteByAggregateIdCommand';
import { MailDeleteCommand } from 'src/stores/mail/commands/MailDeleteCommand';
import { MailSpamByAggregateIdCommand } from 'src/stores/mail/commands/MailSpamByAggregateIdCommand';
import { RemoveReceiverGroupMemberCommand } from 'src/stores/mail/commands/RemoveReceiverGroupMemberCommand';
import { SaveTheMailDraftCommand } from 'src/stores/mail/commands/SaveTheMailDraftCommand';
import { SaveTheMailTemplateCommand } from 'src/stores/mail/commands/SaveTheMailTemplateCommand';
import { SetTheContactPictureForTheMailAddressCommand } from 'src/stores/mail/commands/SetTheContactPictureForTheMailAddressCommand';
import { SetTheStandardMailTemplateForTheAccountCommand } from 'src/stores/mail/commands/SetTheStandardMailTemplateForTheAccountCommand';
import {
	GetReceiverGroupMembersForAuthenticatedUserQuery,
	GetReceiverGroupsForAuthenticatedUserQuery,
	GetTheAttachmentViewerContentForTheMailQuery,
	GetTheMailAccountsAndSignaturesAndDraftsAndTemplatesForTheAuthenticatedUserQuery,
	GetTheMailAddressSuggestionsQuery,
	GetTheMailTemplateOrDraftForTheAuthenticatedUserQuery,
	MailQuery,
} from 'src/stores/mail/queries';

import { CommandProcessIdHandling } from 'src/models/command';
import {
	Mail,
	MailAddressSuggest,
	MailAddressSuggestRunType,
	MailAttachmentPreview,
	MailAttachmentPreviewRunType,
	MailReceiverGroup,
	MailReceiverGroupMember,
	MailReceiverGroupMemberRunType,
	MailReceiverGroupRunType,
	MailRunType,
	MailSendingAccountsAndSignaturesAndDraftsAndTemplates,
	MailSendingAccountsAndSignaturesAndDraftsAndTemplatesRunType,
	MailTemplateOrDraftLoadRunType,
	MailTemplateOrDraftLoaded,
} from 'src/models/mail';
import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';

import { AggregateComparator } from 'src/util/aggregate/AggregateComparator';
import { AggregateMerger } from 'src/util/aggregate/AggregateMerger';
import {
	ApplyTheStatusForTheAggregateIdsPayload,
	aggregateIdWithUnsetSourceAndTargetContainerId,
	applyTheStatusForTheAggregateIdsInRecordMaps,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';
import {
	extractMailDraftIdFromCommandResponse,
	extractMailTemplateIdFromCommandResponse,
} from 'src/util/mail/extractMailDraftIdFromCommandResponse';
import { QueryStore } from 'src/util/query/QueryStore';

import { GetTheMailAccountsAndSignaturesForTheAuthenticatedUserCallback } from './callbacks/GetTheMailAccountsAndSignaturesForTheAuthenticatedUserCallback';
import { GetTheMailAddressSuggestionsCallback } from './callbacks/GetTheMailAddressSuggestionsCallback';
import { GetTheMailTemplateOrDraftForTheAuthenticatedUserQueryCallback } from './callbacks/GetTheMailTemplateOrDraftForTheAuthenticatedUserQueryCallback';
import { GetThePreviewForTheMailAttachmentCallback } from './callbacks/GetThePreviewForTheMailAttachmentCallback';
import { MailCallback } from './callbacks/MailCallback';
import { MailReceiverGroupsCallback } from './callbacks/MailReceiverGroupsCallback';
import {
	AddMailAttachmentPreviewPayload,
	AddMailReceiversToMailReceiverGroupPayload,
	ClearMailAttachmentPreviewsPayload,
	ConvertTheMailAttachmentToDocumentPayload,
	ConvertTheMailAttachmentToDocumentResult,
	CreateMailReceiverGroupPayload,
	DeleteMailReceiverGroupPayload,
	DeleteTheMailByAggregateIdPayload,
	DeleteTheMailPayload,
	DeleteTheTemplateOrDraftPayload,
	FetchMailAccountsAndSignaturesAndDraftsAndTemplatesPayload,
	FetchMailAttachmentPreviewPayload,
	FetchMailPayload,
	FetchMailReceiverGroupMembersPayload,
	FetchMailReceiverGroupsPayload,
	FetchMailTemplateOrDraftPayload,
	MailAttachmentPreviewEntry,
	MailComposeSnapshot,
	MailStoreState,
	RemoveMailReceiverFromMailReceiverGroupPayload,
	ResetPayload,
	SaveTheDraftPayload,
	SaveTheTemplatePayload,
	SetMailReceiverGroupDetailsPayload,
	SetMailReceiverGroupsPayload,
	SetMailTemplateOrDraftPayload,
	SetSelectedMailDraftIdPayload,
	SetSelectedMailTemplateIdPayload,
	SetSuggestedMailAddressPayload,
	SetTheContactPictureForMailAddressPayload,
	SetTheMailStatePayload,
	SetTheStandardTemplateForTheAccountPayload,
	SpamTheMailByAggregateIdPayload,
	SuggestMailAddressPayload,
	UnsubscribeFetchMailPayload,
} from './interfaces';

const normalizeAggregateId = (aggregateId: string): string =>
	aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);

const createDefaultState = (): MailStoreState => ({
	composeDraftSnapshotByIdentity: new Map<string, MailComposeSnapshot>(),
	initialLoadedMails: new Map<string, Mail>(),
	mailAddressSuggestionRequestVersions: new Map<string, number>(),
	mailAttachmentPreviews: new Map<string, MailAttachmentPreviewEntry[]>(),
	mailInvitationQueuedParticipations: new Map<string, MailInvitationParticipation>(),
	mailInvitations: new Map<string, InvitationFacts | null>(),
	mailReceiverGroupDetails: null,
	mailReceiverGroups: [],
	mailSendingAccountsAndSignatures: null,
	mailTemplateOrDraftLoaded: new Map<string, MailTemplateOrDraftLoaded>(),
	mails: new Map<string, Mail>(),
	selectedMailDraftId: new Map<string, string>(),
	selectedMailTemplateId: new Map<string, string>(),
	suggestedMailAddress: new Map<string, MailAddressSuggest[]>(),
});

const resolveStateCommandPayload = (
	payload: SetTheMailStatePayload | string
): { aggregateId: string; commandBus: CommandBus | undefined } => {
	if (typeof payload === 'string') {
		return {
			aggregateId: payload,
			commandBus: inject<CommandBus>('commandBus'),
		};
	}

	return {
		aggregateId: payload.aggregateId,
		commandBus: payload.commandBus ?? inject<CommandBus>('commandBus'),
	};
};

const runMailQuery = async <ResponseType>(
	queryStore: QueryStore | undefined,
	query: Parameters<QueryStore['query']>[0]['query'],
	runType: RuntypeBase<unknown>
): Promise<ResponseType> => {
	if (queryStore === undefined) {
		throw new Error('QueryStore is not available.');
	}

	return await new Promise<ResponseType>((resolve, reject) => {
		void queryStore.query<ResponseType>({
			callback: new MailReceiverGroupsCallback<ResponseType>(resolve, reject),
			createTime: new Date(),
			query: query,
			responseType: 'json',
			runType: runType,
		});
	});
};

const runMailReceiverGroupCommand = async (
	commandBus: CommandBus | undefined,
	command: Parameters<CommandBus['sendCommand']>[0]
): Promise<void> => {
	if (commandBus === undefined) {
		throw new Error('CommandBus is not available.');
	}

	const response = await commandBus.sendCommand(command);
	if (response === null) {
		throw new Error('Mail receiver group command failed.');
	}
};

export const useMailStore = defineStore('mail', {
	actions: {
		addMailAttachmentPreview(payload: AddMailAttachmentPreviewPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const previews = this.mailAttachmentPreviews.get(normalizedAggregateId) ?? [];
			this.mailAttachmentPreviews.set(normalizedAggregateId, [
				...previews,
				{
					filename: payload.filename,
					payload: payload.payload,
					previewKey: payload.previewKey ?? payload.filename,
				},
			]);
		},
		async addMailReceiversToMailReceiverGroup(payload: AddMailReceiversToMailReceiverGroupPayload): Promise<void> {
			await runMailReceiverGroupCommand(
				payload.commandBus,
				new AddReceiverGroupMembersCommand(payload.groupId, payload.emailAddresses)
			);
			await this.fetchMailReceiverGroupMembers({
				groupId: payload.groupId,
				queryStore: payload.queryStore,
			});
			await this.fetchMailReceiverGroups({ queryStore: payload.queryStore });
		},
		applyTheStatusForTheAggregateIds(payload: ApplyTheStatusForTheAggregateIdsPayload): void {
			applyTheStatusForTheAggregateIdsInRecordMaps(
				[this.mails, this.initialLoadedMails],
				payload.aggregateIds,
				payload.status
			);
		},
		clearComposeDraftSnapshot(payload: { identity: string }): void {
			this.composeDraftSnapshotByIdentity.delete(normalizeAggregateId(payload.identity));
		},
		clearMailAttachmentPreviews(payload: ClearMailAttachmentPreviewsPayload): void {
			this.mailAttachmentPreviews.delete(normalizeAggregateId(payload.aggregateId));
		},
		clearSuggestedMailAddress(aggregateId: string): void {
			this.startTheMailAddressSuggestionRequest(aggregateId);
			this.setSuggestedMailAddress({ aggregateId: aggregateId, suggestedMailAddress: [] });
		},
		composeDraftSnapshotForIdentity(identity: string): MailComposeSnapshot | undefined {
			return this.composeDraftSnapshotByIdentity.get(normalizeAggregateId(identity));
		},
		async convertTheMailAttachmentToDocument(
			payload: ConvertTheMailAttachmentToDocumentPayload
		): Promise<ConvertTheMailAttachmentToDocumentResult> {
			if (payload.commandBus === undefined) {
				return 'failed';
			}

			const response = await payload.commandBus.sendCommand<{ data?: { result?: string } }>(
				new ConvertAttachmentToDocumentCommand(payload.storageKey, payload.attachmentId, payload.postboxId)
			);

			const result = response?.data?.data?.result;
			if (result === 'created' || result === 'alreadyConverted') {
				return result;
			}

			return 'failed';
		},
		async createMailReceiverGroup(payload: CreateMailReceiverGroupPayload): Promise<void> {
			await runMailReceiverGroupCommand(payload.commandBus, new CreateReceiverGroupCommand(payload.groupName));
			await this.fetchMailReceiverGroups({ queryStore: payload.queryStore });
		},
		async deleteMailReceiverGroup(payload: DeleteMailReceiverGroupPayload): Promise<void> {
			await runMailReceiverGroupCommand(payload.commandBus, new DeleteReceiverGroupCommand(payload.groupId));
			if (this.mailReceiverGroupDetails?.id === payload.groupId) {
				this.setMailReceiverGroupDetails({ mailReceiverGroupDetails: null });
			}
			await this.fetchMailReceiverGroups({ queryStore: payload.queryStore });
		},
		async deleteTheMail(payload: DeleteTheMailPayload): Promise<void> {
			const mailFromCollection = this.mails.get(normalizeAggregateId(payload.aggregateId));

			if (mailFromCollection === undefined) {
				return;
			}

			await payload.commandBus?.sendCommand(new MailDeleteCommand(mailFromCollection, payload.processId ?? ''));
		},
		async deleteTheMailByAggregateId(payload: DeleteTheMailByAggregateIdPayload): Promise<void> {
			await payload.commandBus?.sendCommand(
				new MailDeleteByAggregateIdCommand(payload.aggregateId, payload.processId ?? '')
			);
		},
		async deleteTheTemplateOrDraft(payload: DeleteTheTemplateOrDraftPayload): Promise<void> {
			await payload.commandBus?.sendCommand(
				new DeleteTheMailTemplateOrDraftCommand(payload.draftOrTemplateId, payload.type)
			);

			await this.fetchMailAccountsAndSignaturesAndDraftsAndTemplates({
				queryStore: payload.queryStore,
			});
		},
		async fetchMail(payload: FetchMailPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<Mail>({
					callback: new MailCallback((mail) => this.setMail(mail)),
					createTime: new Date(),
					query: new MailQuery(payload.aggregateId),
					responseType: 'json',
					runType: MailRunType,
				});
				return;
			}

			this.setMail(null);
		},
		async fetchMailAccountsAndSignaturesAndDraftsAndTemplates(
			payload: FetchMailAccountsAndSignaturesAndDraftsAndTemplatesPayload
		): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<MailSendingAccountsAndSignaturesAndDraftsAndTemplates>({
					callback: new GetTheMailAccountsAndSignaturesForTheAuthenticatedUserCallback(
						(mailAccountsAndSignaturesAndDraftsAndTemplates) =>
							this.setMailAccountsAndSignaturesAndDraftsAndTemplates(
								mailAccountsAndSignaturesAndDraftsAndTemplates
							)
					),
					createTime: new Date(),
					query: new GetTheMailAccountsAndSignaturesAndDraftsAndTemplatesForTheAuthenticatedUserQuery(),
					responseType: 'json',
					runType: MailSendingAccountsAndSignaturesAndDraftsAndTemplatesRunType,
				});
				return;
			}

			this.setMailAccountsAndSignaturesAndDraftsAndTemplates(null);
		},
		async fetchMailAttachmentPreview(payload: FetchMailAttachmentPreviewPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				if (
					(this.mailAttachmentPreviewsForAggregateId(payload.aggregateId) as
						| MailAttachmentPreviewEntry[]
						| undefined) === undefined
				) {
					this.clearMailAttachmentPreviews({
						aggregateId: payload.aggregateId,
					});
				}

				await payload.queryStore.query<MailAttachmentPreview>({
					callback: new GetThePreviewForTheMailAttachmentCallback(
						payload.aggregateId,
						payload.filename,
						payload.storageKey,
						(previewPayload) => this.addMailAttachmentPreview(previewPayload)
					),
					createTime: new Date(),
					query: new GetTheAttachmentViewerContentForTheMailQuery(payload.aggregateId, payload.storageKey),
					responseType: 'blob',
					runType: MailAttachmentPreviewRunType,
				});
				return;
			}

			this.clearMailAttachmentPreviews({
				aggregateId: payload.aggregateId,
			});
		},
		async fetchMailInvitation(payload: FetchMailInvitationPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.resetMailInvitation({ aggregateId: payload.aggregateId });
				return;
			}

			const wrappers = await runMailQuery<MailInvitationQueryWrapper[]>(
				payload.queryStore,
				new GetTheMailInvitationForTheMailQuery(payload.aggregateId),
				MailInvitationQueryWrapperRunType
			);
			const invitation = wrappers[0]?.invitation ?? null;
			const queuedParticipation = this.mailInvitationQueuedParticipations.get(
				normalizeAggregateId(payload.aggregateId)
			);
			this.setMailInvitation({
				aggregateId: payload.aggregateId,
				invitation:
					invitation === null || queuedParticipation === undefined
						? invitation
						: {
								...invitation,
								currentResponse: queuedParticipation,
							},
			});
		},
		async fetchMailReceiverGroupMembers(payload: FetchMailReceiverGroupMembersPayload): Promise<void> {
			const members = await runMailQuery<MailReceiverGroupMember[]>(
				payload.queryStore,
				new GetReceiverGroupMembersForAuthenticatedUserQuery(payload.groupId),
				MailReceiverGroupMemberRunType
			);
			const receiverGroup = this.mailReceiverGroups.find((group) => Number(group.id) === payload.groupId);

			this.setMailReceiverGroupDetails({
				mailReceiverGroupDetails:
					receiverGroup === undefined
						? null
						: {
								id: receiverGroup.id,
								members: members,
								name: receiverGroup.name,
								token: receiverGroup.token,
							},
			});
		},
		async fetchMailReceiverGroups(payload: FetchMailReceiverGroupsPayload): Promise<void> {
			const receiverGroups = await runMailQuery<MailReceiverGroup[]>(
				payload.queryStore,
				new GetReceiverGroupsForAuthenticatedUserQuery(),
				MailReceiverGroupRunType
			);
			this.setMailReceiverGroups({ mailReceiverGroups: receiverGroups });
		},
		async fetchMailTemplateOrDraft(payload: FetchMailTemplateOrDraftPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<MailTemplateOrDraftLoaded>({
					callback: new GetTheMailTemplateOrDraftForTheAuthenticatedUserQueryCallback(
						payload.aggregateId,
						(mailTemplateOrDraftPayload) => this.setMailTemplateOrDraft(mailTemplateOrDraftPayload)
					),
					createTime: new Date(),
					query: new GetTheMailTemplateOrDraftForTheAuthenticatedUserQuery(payload.draftOrTemplateId),
					responseType: 'json',
					runType: MailTemplateOrDraftLoadRunType,
				});
				return;
			}

			this.setMail(null);
		},
		mail(aggregateId: string): Mail | undefined {
			return this.mails.get(normalizeAggregateId(aggregateId));
		},
		mailAttachmentPreviewsForAggregateId(aggregateId: string): MailAttachmentPreviewEntry[] {
			return this.mailAttachmentPreviews.get(normalizeAggregateId(aggregateId)) ?? [];
		},
		mailInvitation(aggregateId: string): InvitationFacts | null | undefined {
			return this.mailInvitations.get(normalizeAggregateId(aggregateId));
		},
		mailTemplateOrDraft(aggregateId: string): MailTemplateOrDraftLoaded | undefined {
			return this.mailTemplateOrDraftLoaded.get(normalizeAggregateId(aggregateId));
		},
		async refetchMailInvitation(payload: RefetchMailInvitationPayload): Promise<void> {
			await this.fetchMailInvitation(payload);
		},
		async removeMailReceiverFromMailReceiverGroup(
			payload: RemoveMailReceiverFromMailReceiverGroupPayload
		): Promise<void> {
			await runMailReceiverGroupCommand(
				payload.commandBus,
				new RemoveReceiverGroupMemberCommand(payload.groupId, payload.memberId)
			);
			await this.fetchMailReceiverGroupMembers({
				groupId: payload.groupId,
				queryStore: payload.queryStore,
			});
			await this.fetchMailReceiverGroups({ queryStore: payload.queryStore });
		},
		reset(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			this.mails.delete(normalizedAggregateId);
			this.initialLoadedMails.delete(normalizedAggregateId);
			this.mailAttachmentPreviews.delete(normalizedAggregateId);
			this.mailInvitations.delete(normalizedAggregateId);
			this.mailInvitationQueuedParticipations.delete(normalizedAggregateId);
			this.mailTemplateOrDraftLoaded.delete(normalizedAggregateId);
			this.suggestedMailAddress.delete(normalizedAggregateId);
			this.mailAddressSuggestionRequestVersions.delete(normalizedAggregateId);
			this.selectedMailTemplateId.delete(normalizedAggregateId);
			this.selectedMailDraftId.delete(normalizedAggregateId);
			this.mailSendingAccountsAndSignatures = null;
			this.mailReceiverGroupDetails = null;
			this.mailReceiverGroups = [];
		},
		resetMailInvitation(payload: { aggregateId: string }): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			this.mailInvitations.delete(normalizedAggregateId);
			this.mailInvitationQueuedParticipations.delete(normalizedAggregateId);
		},
		async respondToMailInvitation(payload: RespondToMailInvitationPayload): Promise<RespondToMailInvitationResult> {
			if (payload.commandBus === undefined) {
				return { result: 'failed' };
			}

			try {
				const response = await payload.commandBus.sendCommand<{
					data?: {
						replyLinkStored?: unknown;
						result?: unknown;
						warning?: unknown;
					};
					success?: unknown;
				}>(
					new RespondToTheMailInvitationCommand(
						payload.aggregateId,
						payload.mailId,
						payload.invitationId,
						payload.sendingAccountId,
						payload.participation
					)
				);
				const commandResponse = response?.data;
				const result = commandResponse?.data;
				if (
					commandResponse?.success !== true ||
					result?.result !== 'queued' ||
					typeof result.replyLinkStored !== 'boolean' ||
					(result.warning !== null && result.warning !== 'LOCAL_RESPONSE_STATE_NOT_STORED')
				) {
					return { result: 'failed' };
				}

				const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
				this.mailInvitationQueuedParticipations.set(normalizedAggregateId, payload.participation);

				const invitation = this.mailInvitations.get(normalizedAggregateId);
				if (invitation !== undefined && invitation !== null) {
					this.setMailInvitation({
						aggregateId: payload.aggregateId,
						invitation: {
							...invitation,
							currentResponse: payload.participation,
						},
					});
				}

				return {
					replyLinkStored: result.replyLinkStored,
					result: 'queued',
					warning: result.warning,
				};
			} catch {
				return { result: 'failed' };
			}
		},
		async saveTheDraft(payload: SaveTheDraftPayload): Promise<string | undefined> {
			const result = await payload.commandBus?.sendCommand<{ data?: { draftId?: number | string } }>(
				new SaveTheMailDraftCommand(
					payload.draftName,
					payload.draftId,
					payload.autoSave,
					payload.senderAccountId,
					payload.managedSignatureId,
					payload.subject,
					payload.content,
					payload.receiversTo,
					payload.receiversCC,
					payload.receiversBCC,
					payload.attachmentIds,
					payload.originalAttachmentIds,
					payload.replyOrForwardMailAggregateId,
					payload.replyOrForwardType
				)
			);

			if (result === null || result === undefined) {
				return undefined;
			}

			const draftId = extractMailDraftIdFromCommandResponse(result);
			if (draftId === undefined) {
				return undefined;
			}

			this.setSelectedMailDraftId({
				aggregateId: payload.aggregateId,
				selectedMailDraftId: draftId,
			});

			if (payload.autoSave !== 'true') {
				await this.fetchMailAccountsAndSignaturesAndDraftsAndTemplates({
					queryStore: payload.queryStore,
				});
			}

			return draftId;
		},
		async saveTheTemplate(payload: SaveTheTemplatePayload): Promise<void> {
			const result = await payload.commandBus?.sendCommand<{ data?: { templateId?: string } }>(
				new SaveTheMailTemplateCommand(
					payload.templateName,
					payload.templateId,
					payload.senderAccountId,
					payload.managedSignatureId,
					payload.subject,
					payload.content,
					payload.receiversTo,
					payload.receiversCC,
					payload.receiversBCC,
					payload.attachmentIds,
					payload.originalAttachmentIds,
					payload.replyOrForwardMailAggregateId,
					payload.replyOrForwardType
				)
			);

			const templateId = extractMailTemplateIdFromCommandResponse(result);
			if (templateId !== undefined) {
				this.setSelectedMailTemplateId({
					aggregateId: payload.aggregateId ?? payload.templateId,
					selectedMailTemplateId: templateId,
				});
			}

			await this.fetchMailAccountsAndSignaturesAndDraftsAndTemplates({
				queryStore: payload.queryStore,
			});
		},
		selectedMailDraftIdForAggregateId(aggregateId: string): string | undefined {
			return this.selectedMailDraftId.get(normalizeAggregateId(aggregateId));
		},
		selectedMailTemplateIdForAggregateId(aggregateId: string): string | undefined {
			return this.selectedMailTemplateId.get(normalizeAggregateId(aggregateId));
		},
		setComposeDraftSnapshot(payload: { identity: string; snapshot: MailComposeSnapshot }): void {
			this.composeDraftSnapshotByIdentity.set(normalizeAggregateId(payload.identity), payload.snapshot);
		},
		setMail(payload: Mail | null): void {
			if (payload === null) {
				return;
			}

			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const mailFromCollection = this.mails.get(normalizedAggregateId);
			const initialLoadedMailFromCollection = this.initialLoadedMails.get(normalizedAggregateId);

			if (mailFromCollection !== undefined && !AggregateComparator.isEqual(mailFromCollection, payload)) {
				return;
			}

			if (
				mailFromCollection !== undefined &&
				initialLoadedMailFromCollection !== undefined &&
				!AggregateComparator.isEqual(initialLoadedMailFromCollection, payload)
			) {
				const mergedMail = AggregateMerger.merge(
					initialLoadedMailFromCollection,
					mailFromCollection,
					payload
				) as Mail;
				this.mails.set(normalizeAggregateId(mergedMail.aggregateId), cloneDeep(mergedMail));
			} else {
				this.mails.set(normalizedAggregateId, payload);
			}

			this.initialLoadedMails.set(normalizedAggregateId, cloneDeep(payload));
		},
		setMailAccountsAndSignaturesAndDraftsAndTemplates(
			payload: MailSendingAccountsAndSignaturesAndDraftsAndTemplates | null
		): void {
			this.mailSendingAccountsAndSignatures = payload;
		},
		setMailInvitation(payload: { aggregateId: string; invitation: InvitationFacts | null }): void {
			this.mailInvitations.set(normalizeAggregateId(payload.aggregateId), payload.invitation);
		},
		setMailReceiverGroupDetails(payload: SetMailReceiverGroupDetailsPayload): void {
			this.mailReceiverGroupDetails = payload.mailReceiverGroupDetails;
		},
		setMailReceiverGroups(payload: SetMailReceiverGroupsPayload): void {
			this.mailReceiverGroups = payload.mailReceiverGroups;
		},
		setMailTemplateOrDraft(payload: SetMailTemplateOrDraftPayload): void {
			this.mailTemplateOrDraftLoaded.set(
				normalizeAggregateId(payload.aggregateId),
				payload.mailTemplateOrDraftLoaded
			);
		},
		setSelectedMailDraftId(payload: SetSelectedMailDraftIdPayload): void {
			this.selectedMailDraftId.set(normalizeAggregateId(payload.aggregateId), payload.selectedMailDraftId);
		},
		setSelectedMailTemplateId(payload: SetSelectedMailTemplateIdPayload): void {
			this.selectedMailTemplateId.set(normalizeAggregateId(payload.aggregateId), payload.selectedMailTemplateId);
		},
		setSuggestedMailAddress(payload: SetSuggestedMailAddressPayload): void {
			this.suggestedMailAddress.set(normalizeAggregateId(payload.aggregateId), payload.suggestedMailAddress);
		},
		async setTheContactPictureForMailAddress(payload: SetTheContactPictureForMailAddressPayload): Promise<void> {
			const response = await payload.commandBus?.sendCommand(
				new SetTheContactPictureForTheMailAddressCommand(
					payload.contactMailAddress,
					payload.contactPictureVersion,
					payload.payload
				)
			);

			if (response === null || response === undefined) {
				throw new Error('Contact picture command failed');
			}
		},
		setTheMailStateToReady(payload: SetTheMailStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.READY
				)
			);
		},
		setTheMailStateToSeen(payload: SetTheMailStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.SEEN
				)
			);
		},
		setTheMailStateToUnseen(payload: SetTheMailStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.UNSEEN
				)
			);
		},
		/**
		 * Setzt (`templateId > 0`) oder entfernt (`templateId = 0`) die Standardvorlage fuer ein Sendekonto
		 * und laedt die Konten neu, damit `standardTemplateId` der Sendekonten aktuell ist.
		 *
		 * Der Command-Bus schluckt Transportfehler; als Erfolgsnachweis gilt deshalb allein der neu geladene
		 * Kontostand. Weicht er ab (Backend kennt den Command nicht, Validierung abgelehnt), wird verworfen.
		 */
		async setTheStandardTemplateForTheAccount(payload: SetTheStandardTemplateForTheAccountPayload): Promise<void> {
			if (payload.commandBus === undefined) {
				return;
			}

			await payload.commandBus.sendCommand(
				new SetTheStandardMailTemplateForTheAccountCommand(payload.postboxId, payload.templateId)
			);

			if (payload.queryStore === undefined) {
				return;
			}

			await this.fetchMailAccountsAndSignaturesAndDraftsAndTemplates({
				queryStore: payload.queryStore,
			});

			const account = this.mailSendingAccountsAndSignatures?.sendingAccounts.find(
				(sendingAccount) => Number(sendingAccount.id) === payload.postboxId
			);

			if (account === undefined || account.standardTemplateId !== payload.templateId) {
				throw new Error(
					`The standard template ${payload.templateId} was not applied to postbox ${payload.postboxId}`
				);
			}
		},
		async spamTheMailByAggregateId(payload: SpamTheMailByAggregateIdPayload): Promise<void> {
			await payload.commandBus?.sendCommand(
				new MailSpamByAggregateIdCommand(payload.aggregateId, payload.processId ?? '')
			);
		},
		startTheMailAddressSuggestionRequest(aggregateId: string): number {
			const normalizedAggregateId = normalizeAggregateId(aggregateId);
			const requestVersion = (this.mailAddressSuggestionRequestVersions.get(normalizedAggregateId) ?? 0) + 1;
			this.mailAddressSuggestionRequestVersions.set(normalizedAggregateId, requestVersion);

			return requestVersion;
		},
		async suggestMailAddress(payload: SuggestMailAddressPayload): Promise<boolean> {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const requestVersion = this.startTheMailAddressSuggestionRequest(payload.aggregateId);

			this.setSuggestedMailAddress({
				aggregateId: payload.aggregateId,
				suggestedMailAddress: [],
			});

			if (payload.queryStore === undefined) {
				return false;
			}

			let hasSucceeded = false;

			await payload.queryStore.query<MailAddressSuggest[]>({
				callback: new GetTheMailAddressSuggestionsCallback(
					payload.aggregateId,
					(suggestedMailAddressPayload) => {
						if (this.mailAddressSuggestionRequestVersions.get(normalizedAggregateId) !== requestVersion) {
							return;
						}

						hasSucceeded = true;
						this.setSuggestedMailAddress(suggestedMailAddressPayload);
					}
				),
				createTime: new Date(),
				query: new GetTheMailAddressSuggestionsQuery(payload.searchTerm),
				responseType: 'json',
				runType: MailAddressSuggestRunType,
			});

			return hasSucceeded;
		},
		suggestedMailAddressForAggregateId(aggregateId: string): MailAddressSuggest[] | undefined {
			return this.suggestedMailAddress.get(normalizeAggregateId(aggregateId));
		},
		async unsubscribeFetchMail(payload: UnsubscribeFetchMailPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.unsubscribeQuery(new MailQuery(payload.aggregateId));
			}

			this.setMail(null);
		},
		async unsubscribeFetchMailInvitation(payload: UnsubscribeFetchMailInvitationPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.unsubscribeQuery(new GetTheMailInvitationForTheMailQuery(payload.aggregateId));
			}

			this.resetMailInvitation({ aggregateId: payload.aggregateId });
		},
	},
	getters: {
		mailAccountsAndSignaturesAndDraftsAndTemplates(
			state
		): MailSendingAccountsAndSignaturesAndDraftsAndTemplates | null {
			return state.mailSendingAccountsAndSignatures;
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
